import { Component, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { timer } from "rxjs";
import { Router } from "@angular/router";
import { DateTime } from "luxon";
import { DatePipe, CurrencyPipe } from "@angular/common";
import { ActivatedRoute, Params } from "@angular/router";
import { AbstractUserComponent } from "./abstract/user.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { LegendPosition } from "@swimlane/ngx-charts";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { ContractStatus } from "../obj/ContractStatus";
import { User } from "../entity/User";
import { ConfirmationDialog } from "./incs/confirmation-dialog.component";
import { KilowantContract } from "../entity/KilowantContract";
import { SellerTermsViewDialogComponent } from "./incs/seller-terms-view-dialog.component";
import { CurrentContractData } from "../obj/CurrentContractData";
import { RateData } from "../obj/RateData";
import { Contract } from "../entity/Contract";
import { ExistingContract } from "../entity/ExistingContract";
import { Account } from "../entity/Account";
import { HeaderComponent } from "./incs/header.component";
import { UserHomePriceStatisticsComponent } from "./incs/user-home-price-statistics.component";
import { KilowantBarHomeAcceptOfferChartComponent } from "./chart/kilowant-charts-home-accept-offer.component";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ConsumptionDataType } from "../obj/ConsumptionDataType";
import { Serie } from "../obj/Serie";

@Component({
    selector: "kilowant-view-contract",
    templateUrl: "../../html/main-view-contract.html",
    imports: [HeaderComponent, UserHomePriceStatisticsComponent, KilowantBarHomeAcceptOfferChartComponent, CurrencyPipe, DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewContractComponent extends AbstractUserComponent {
    @ViewChild("body") bodyElement: ElementRef;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;

    contract: KilowantContract | null = null;
    contractTerms: SafeHtml | null = null;
    existingContract: ExistingContract | null = null;
    isSeller: boolean = false;
    linkHome: string = "";
    title: string = "";
    viewBuyer: boolean = false;
    viewSeller: boolean = false;
    viewTerms: boolean = false;
    viewSummary: boolean = false;
    viewDisclosureStatement: boolean = false;

    contractId: number = 0;

    priceHistoryLoaded: boolean = false;
    contractSaving: CurrentContractData | null = null;

    selectedDatasets: number[] = [];
    latestUtilityDate: string = "";
    latestCommunityDate: string = "";
    utilityAverageForHistory: number = 0;
    communityAverageForHistory: number = 0;

    view: [number, number] = [400, 250];
    legend: boolean = true;
    legendTitle: string = "";
    legendPosition: LegendPosition = LegendPosition.Below;
    showLabels: boolean = true;
    animations: boolean = true;
    xAxis: boolean = true;
    yAxis: boolean = false;
    showYAxisLabel: boolean = false;
    showXAxisLabel: boolean = false;
    xAxisLabel: string = "Year";
    yAxisLabel: string = "";
    colorScheme = { domain: ["#D0D0D0", "#A0A0A0"], name: "kilowant", selectable: true, group: null! };
    loadedRates: RateData[] = [];
    isKilowantContract: boolean = false;
    editMode: boolean = false;

    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        protected datepipe: DatePipe,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        private sanitizer: DomSanitizer
    ) {
        super(router, route, dialog, analyticsService, generalService, authService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    pageStart(): void {
        this.route.url.subscribe(params => {
            this.isKilowantContract = (params[0].path == "view-priceplan" || params[0].path == "view-contract");
            this.cdr.markForCheck();
        })
        if (this.user.isSupplier) {
            this.isSeller = true;
            this.linkHome = "/seller/home/accepted-offers";
        } else {
            this.isSeller = false;
          this.linkHome = "/priceplans/list";
        }
        this.cdr.markForCheck();
        this.route.params.forEach((params: Params) => {
            this.contractId = Number(params["id"]);
            if (this.contractId == 0) {
                this.goto("/user/home");
                return;
            }
        });
        if (this.user.isAdvisor && this.generalService.getStoredClient() == null && this.isKilowantContract) {
            this.loadClientFromContract();
        } else {
            this.loadContract(true);
        }
    }

    loadContract(tryToLoadClient: boolean): void {
        if (this.isKilowantContract) {
            this.generalService.getContract(this.contractId)
                .then(contract => {
                    if (contract != null) {
                        this.contract = contract;
                        this.title = "View Price Plan";
                        if (this.contract.terms != null && this.contract.terms.length > 0) {
                            this.contractTerms = this.sanitizer
                                .bypassSecurityTrustHtml(this.contract.terms);
                        }                        
                        this.cdr.markForCheck();
                        if (!this.isSeller) {
                            this.setBackLinkTimeFrame(<KilowantContract>this.contract);
                            let t = this;
                            this.eventSubscriptions.push(timer(100).subscribe((count: any) => {
                                t.loadChart();
                                return;
                            })
                            );
                            this.loadEstimatedDates();
                            this.selectedDatasets.push(1);
                            if (this.contract.accounts[0].isCommunity) {
                                this.selectedDatasets.push(2);
                            }
                            this.cdr.markForCheck();
                        }
                    } else if (tryToLoadClient) {
                        this.loadClientFromContract();
                    }
                });
        } else {
            this.generalService.getExistingContract(this.contractId)
                .then(contract => {
                    this.existingContract = contract;
                    this.title = "View Price Plan";
                    this.cdr.markForCheck();

                    let t = this;
                    this.eventSubscriptions.push(timer(100).subscribe((count: any) => {
                        t.loadChart();
                        return;
                    })
                    );
                    this.loadEstimatedDates();
                    this.selectedDatasets.push(1);
                    if (this.existingContract.accounts[0].isCommunity) {
                        this.selectedDatasets.push(2);
                    }
                    this.cdr.markForCheck();
                });
        }
    }

    loadClientFromContract(): void {
        this.generalService.getClientByContractId(this.contractId).then((c: User | null) => {
            if (c == null) {
                this.goto("/user/home");
                return;
            }
            this.generalService.switchStoredClient(c, null);
            this.loadContract(false);
        }
        );
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    contractIsSigned(status: ContractStatus): boolean {
        return (status == ContractStatus.PENDING) || (status == ContractStatus.OPEN) || (status == ContractStatus.CLOSED);
    }

    contractIsNonVerified(status: ContractStatus): boolean {
        return (status == ContractStatus.PENDING_NON_VERIFIED);
    }

    viewSellersTermsConditions(contract: KilowantContract): void {
        var viewDialogRef = this.dialog.open(SellerTermsViewDialogComponent, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        viewDialogRef.componentInstance.contract = contract;
        viewDialogRef.afterClosed().subscribe(result => {
            viewDialogRef = null!;
        });
    }

    loadChart(): void {
        if (this.bodyElement != null) {
            let c: Contract;
            if (this.isKilowantContract && this.contract) {
                c = this.contract;
            } else if (this.existingContract) {
                c = this.existingContract;
            } else {
                return;
            }
            this.view[0] = this.bodyElement.nativeElement.offsetWidth;
            let promises = [];
            var showUtility = !c.accounts[0].isCommunity;
            var showCommunity = c.accounts[0].isCommunity;
            if (!this.isKilowantContract && c.accounts[0].isCommunity) {
                showUtility = true;
            }
            promises.push(this.generalService.getHistoricalRatesPerAccount(c.accounts[0], c.termFrom, c.termTo, false, true, false, true, showUtility, showCommunity));
            if (this.isKilowantContract) {
                promises.push(this.generalService.getContractSavings(c.id));
            } else {
                promises.push(this.generalService.getExistingContractSavings(c.id));
            }
            Promise.all(promises).then(
                (data: any) => {
                    this.loadedRates = data[0];
                    this.contractSaving = data[1];
                    for (let i = 0; i < this.loadedRates.length; i++) {
                        let priceVal = (this.isKilowantContract) ? this.contract!.getSalesPrice() : this.existingContract!.getSalesPrice();
                        this.loadedRates[i].series.push(new Serie("Price Plan " + (new CurrencyPipe("en-US")).transform(priceVal, "USD", "symbol", "1.3-3") + "/kWh", priceVal));
                    }
                    this.priceHistoryLoaded = true;
                    this.cdr.markForCheck();
                });
        }
    }

    loadEstimatedDates(): void {
        var accountId = 0;
        if (this.isKilowantContract && this.contract) {
            accountId = this.contract.accounts[0].id;
        } else if (this.existingContract) {
            accountId = this.existingContract.accounts[0].id;
        } else {
            return;
        }
        this.generalService.getAccountGetLatestUtilityDate(accountId).then(x => {
            if (x.key != null) {
                this.latestUtilityDate = this.datepipe.transform(x.key, "MMM. y")!;
            }
            if (x.value != null) {
                this.latestCommunityDate = this.datepipe.transform(x.value, "MMM. y")!;
            }
            this.cdr.markForCheck();
        });
    }

    getAccountNumbers(accounts: Account[]): string {
        let outStr = "";
        for (let i = 0; i < accounts.length; i++) {
            outStr += accounts[i].accountNumber;
            if (i < accounts.length - 1) {
                outStr += ", ";
            }
        }
        if (outStr.length == 0) {
            return "-";
        }
        return outStr;
    }

    getSubscribedEmail(user: User | null): string {
        if (user == null) {
            return "-";
        }
        return user.getEmail();
    }

    getRateClasses(accounts: Account[]): string {
        let rateClasses: string[] = [];
        for (let i = 0; i < accounts.length; i++) {
            if (accounts[i].rateClass != null && !rateClasses.includes(accounts[i].rateClass?.name!)) {
                rateClasses.push(accounts[i].rateClass?.name!);
            }
        }
        let outStr = "";
        for (let i = 0; i < rateClasses.length; i++) {
            outStr += rateClasses[i];
            if (i < rateClasses.length - 1) {
                outStr += ", ";
            }
        }

        if (outStr.length == 0) {
            return "-";
        }
        return outStr;
    }

    exportContract(contract: KilowantContract): void {
        var a = document.createElement("a");
        document.body.appendChild(a);
        this.generalService.getContractAsPDF(contract.id)
            .subscribe((data: any) => {
                var blob = new Blob([data], { type: "octet/stream" });
                var url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = "kilowant-" + contract.id +".pdf";
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            });
    }

    cancelContract(contract: KilowantContract): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmTitle = "Cancel Price Plan";
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to cancel this price plan?";

        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.cancelContract(contract.id)
                    .subscribe((res: any) => {
                        this.router.navigateByUrl(this.linkHome);
                    },
                        err => {
                        });
            }
            this.dialogRef = null!;
        });
    }

    editContract(contract: ExistingContract): void {
      this.router.navigate(["/priceplans/details", contract.id]);
    }

    deleteExistingContract(existingContract: ExistingContract): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmTitle = "Delete Price Plan";
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to delete this price plan?"

        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.deleteExistingContract(existingContract.id)
                    .subscribe((res: any) => {
                        this.router.navigateByUrl(this.linkHome);
                    },
                        err => {

                        });
            }
            this.dialogRef = null!;
        });
    }

    setBackLinkTimeFrame(contract: KilowantContract): void {
        if (this.isSeller || this.linkHome.includes("#")) {
            return;
        }
        if (contract.isCancelled()) {
            this.linkHome += "#closed";
            this.cdr.markForCheck();
            return;
        }
        this.refreshTime().then(x => {
            let contractTermFrom = DateTime.fromJSDate(this.contract!.termFrom);
            let contractTermTo = DateTime.fromJSDate(this.contract!.termTo).minus({ days: 1 });
            if (contractTermFrom > this.currentDate) {
                this.linkHome += "#pending";
            } else if (contractTermTo >= this.currentDate) {
                this.linkHome += "#open";
            } else {
                this.linkHome += "#closed";
            }
            this.cdr.markForCheck();
        });
    }

    cancelledBy(contract: KilowantContract): string {
        let res = "";
        if (contract.cancelledDate != null) {
            res += "on " + this.datepipe.transform(contract.cancelledDate, "shortDate") + " @ " + this.datepipe.transform(contract.cancelledDate, "shortTime");
            if (contract.cancelledByBuyer) {
                res += " by " + this.user.firstName + " " + this.user.lastName;
            } else if (contract.cancelledSellerUser != null) {
                res += " by " + contract.cancelledSellerUser.firstName + " " + contract.cancelledSellerUser.lastName;
            }
        }
        return res;
    }

    getAccountIdent(a: Account): string {
        return Account.accountIdent(a);
    }

    downloadFileFromView(id: number) {
        this.generalService.openDocument(id);
    }

    getRatingText(utilityDate: string | null, communityDate: string | null): string {
        let text = "<h2>How is Savings Calculated?</h2>Savings based upon weighted average difference between the offer rate and utility rate for the same term (apples to apples comparison).<br/><br/>";
        let p = 1;
        let asterikVals: string[] = [];

        if (this.loadedRates.length > 0) {
            for (let x = 0; x < this.loadedRates.length; x++) {
                for (let i = 0; i < this.loadedRates[x].series.length; i++) {
                    let testVal = this.loadedRates[x].series[i].name.replace(/\s*\(Projected\)\s*/, "");
                    if (testVal == "RI Energy") {
                        if (!asterikVals.includes(testVal)) {
                            asterikVals.push(testVal);
                        }
                    }
                    if (testVal == "NextEra Energy") {
                        if (!asterikVals.includes(testVal)) {
                            asterikVals.push(testVal);
                        }
                    }
                }
            }
        }

        if (utilityDate != null && utilityDate.length > 0 && asterikVals.includes("RI Energy")) {
            text += this.repeater("*", p++) + " RI Energy Last Resort Service rate beyond " + utilityDate + " is estimated and subject to adjustment<br/>";
        }
        if (communityDate != null && communityDate.length > 0 && asterikVals.includes("NextEra Energy")) {
            text += this.repeater("*", p++) + " NextEra Energy rate beyond " + utilityDate + " is estimated and subject to adjustment<br/>";
        }
        return text;
    }

    repeater(chr: string, num: number) {
        let outStr = "";
        for (let i = 0; i < num; i++) {
            outStr = outStr + chr;
        }
        return outStr;
    }
    ifPartlyText(dataType: ConsumptionDataType|null): string {
        if (dataType!=null && dataType == ConsumptionDataType.PARTLY) {
            return "*";
        }
        return "";
    }
}
